import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { expressURL, axiosInstance } from '../../../../services/api';

const REPORT_TYPES = [
  {
    id: 'invoice',
    name: 'Invoice Team Email',
    icon: DescriptionIcon,
    description: 'Configure email settings for invoice team emails',
    scenarioKey: 'invoice',
    scenarioType: 'invoice-send-to-team',
    scenarioName: 'Invoice send to team',
  },
  {
    id: 'welcome-emails',
    name: 'Welcome Emails',
    icon: AssessmentIcon,
    description: 'Configure email settings for client and investor emails',
    scenarioKey: 'welcome-emails',
    scenarioType: 'welcome-emails',
    scenarioName: 'welcome-emails',
  }
];

const EmailSettings = () => {
  const [emailSettings, setEmailSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [formData, setFormData] = useState({
    to: [{ email: '', name: '' }],
    cc: [{ email: '', name: '' }],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (selectedReportType) {
      fetchEmailSettings(selectedReportType);
    } else {
      setLoading(false);
    }
  }, [selectedReportType]);

  const fetchEmailSettings = async (reportType) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${expressURL}/api/email-settings/${reportType.id}`);
      if (response.data) {
        setEmailSettings(response.data);
        setFormData(response.data);
      } else {
        // If no settings exist, prepare default data
        const defaultData = {
          scenarioKey: reportType.scenarioKey,
          scenarioType: reportType.scenarioType,
          scenarioName: reportType.scenarioName,
          description: `Sends ${reportType.name.toLowerCase()} to team members`,
          triggerType: "scheduled",
          to: [{ email: '', name: '' }],
          cc: [{ email: '', name: '' }],
          isActive: true
        };
        setEmailSettings(defaultData);
        setFormData(defaultData);
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
      // If error is 404, create new settings
      if (error.response?.status === 404) {
        const defaultData = {
          scenarioKey: reportType.scenarioKey,
          scenarioType: reportType.scenarioType,
          scenarioName: reportType.scenarioName,
          description: `Sends ${reportType.name.toLowerCase()} to team members`,
          triggerType: "scheduled",
          to: [{ email: '', name: '' }],
          cc: [{ email: '', name: '' }],
          isActive: true
        };
        setEmailSettings(null);
        setFormData(defaultData);
        setEditMode(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], { email: '', name: '' }]
    }));
  };

  const handleRemoveRecipient = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (type, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].map((recipient, i) =>
        i === index ? { ...recipient, [field]: value } : recipient
      )
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const endpoint = emailSettings?._id
        ? `${expressURL}/api/email-settings/${emailSettings._id}`
        : `${expressURL}/api/email-settings`;

      const method = emailSettings?._id ? 'put' : 'post';
      const payload = {
        ...formData,
        ...(selectedReportType ? {
          scenarioKey: formData.scenarioKey ?? selectedReportType.scenarioKey,
          scenarioType: formData.scenarioType ?? selectedReportType.scenarioType,
          scenarioName: formData.scenarioName ?? selectedReportType.scenarioName,
        } : {})
      };

      const response = await axiosInstance[method](endpoint, payload);

      setEmailSettings(response.data);
      setEditMode(false);
      setSnackbar({
        open: true,
        message: 'Email settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving email settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving email settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const isWelcomeType = selectedReportType?.id === 'welcome-emails';
  const hasToEmails = (formData?.to || []).some(r => r?.email);
  const isToEditable = editMode && (!isWelcomeType || (isWelcomeType && !hasToEmails));

  const renderReportTypeSelection = () => (
    <Grid container spacing={3}>
      {REPORT_TYPES.map((reportType) => {
        const Icon = reportType.icon;
        return (
          <Grid item xs={12} sm={6} md={4} key={reportType.id}>
            <Card>
              <CardActionArea onClick={() => setSelectedReportType(reportType)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icon sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h6" component="h2">
                      {reportType.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {reportType.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      {selectedReportType ? (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => {
                  setSelectedReportType(null);
                  setEditMode(false);
                  setEmailSettings(null);
                }}
                sx={{ mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="h2">
                {selectedReportType.name} Email Settings
              </Typography>
            </Box>
            <Button
              variant="contained"
              color={editMode ? "error" : "primary"}
              startIcon={editMode ? <CancelIcon /> : <EditIcon />}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? "Cancel" : "Edit Settings"}
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* To Recipients */}
            <Grid item xs={12}>
              {isWelcomeType ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Primary recipients are taken from the client/investor creation form and cannot be added here.
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    To Recipients
                  </Typography>
                  {formData.to.map((recipient, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <TextField
                        label="Name"
                        value={recipient.name}
                        onChange={(e) => handleInputChange('to', index, 'name', e.target.value)}
                        disabled={!isToEditable}
                        size="small"
                      />
                      <TextField
                        label={isWelcomeType && !isToEditable ? 'Email (auto-populated)' : 'Email'}
                        value={recipient.email}
                        onChange={(e) => handleInputChange('to', index, 'email', e.target.value)}
                        disabled={!isToEditable}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      {editMode && (
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveRecipient('to', index)}
                          disabled={formData.to.length === 1 || (isWelcomeType && !isToEditable)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {editMode && isToEditable && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => handleAddRecipient('to')}
                      sx={{ mt: 1 }}
                    >
                      Add Recipient
                    </Button>
                  )}
                </>
              )}
            </Grid>

            {/* CC Recipients */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                CC Recipients
              </Typography>
              {formData.cc.map((recipient, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Name"
                    value={recipient.name}
                    onChange={(e) => handleInputChange('cc', index, 'name', e.target.value)}
                    disabled={!editMode}
                    size="small"
                  />
                  <TextField
                    label="Email"
                    value={recipient.email}
                    onChange={(e) => handleInputChange('cc', index, 'email', e.target.value)}
                    disabled={!editMode}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  {editMode && (
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveRecipient('cc', index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              {editMode && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleAddRecipient('cc')}
                  sx={{ mt: 1 }}
                >
                  Add CC Recipient
                </Button>
              )}
            </Grid>

            {/* Save Button */}
            {editMode && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    Save Settings
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Select Email Type
          </Typography>
          {renderReportTypeSelection()}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EmailSettings;